import Link from 'next/link'
import { useState } from 'react'
import { ArrowRightIcon, ExternalLinkIcon } from 'lucide-react'

import { CMSLink } from '@/components/Link'
import { Media } from '@/components/Media'
import { Button } from '@/components/ui/button'
import { useHeaderNavigationContext } from '@/providers/HeaderNavigation'

import type { Header as HeaderType } from '@/payload-types'

export const NavMobile: React.FC<{ data: HeaderType }> = ({ data }) => {
  const main = data?.centerNav?.main
  const feature = data?.dropdown?.feature
  const secondary = data?.dropdown?.secondary

  // The nav is always mounted (only translated off-screen), so don't pay for
  // the decorative background until the menu is first opened. Stays mounted
  // afterward so re-opens are instant.
  const { isMenuOpen } = useHeaderNavigationContext()
  const [hasOpened, setHasOpened] = useState(false)
  if (isMenuOpen && !hasOpened) setHasOpened(true)

  return (
    <div className="relative min-h-0 w-full flex-1 overflow-y-auto" data-lenis-prevent>
      <div className="relative flex min-h-full flex-col justify-between gap-0 pb-6">
        <div className="main relative z-2 mx-4 mt-2 flex flex-col gap-4 rounded-xl px-4 py-6 text-center">
          {main &&
            main.map((item) => (
              <Button
                key={item.id}
                variant="link"
                icon={false}
                asChild
                className="text-3xl! text-accent!"
              >
                {item.link.type === 'reference' ? (
                  <CMSLink {...item.link}>
                    <ArrowRightIcon className="size-4" />
                  </CMSLink>
                ) : (
                  <Link
                    href={item.link.url || '/'}
                    target={item.link.newTab ? '_blank' : '_self'}
                    rel={item.link.newTab ? 'noopener noreferrer' : undefined}
                  >
                    {item.link.label}
                    <ArrowRightIcon className="size-4" />
                  </Link>
                )}
              </Button>
            ))}
          <Button variant="link" icon={false} asChild className="text-3xl! text-accent!">
            <Link href="/search">
              Search
              <ArrowRightIcon className="size-4" />
            </Link>
          </Button>
          <Button variant="accent" icon={false} asChild className="w-full">
            <Link href="/donate">Donate</Link>
          </Button>
          <Button variant="accent" icon={false} asChild className="w-full">
            <Link href="/join">Become a Member</Link>
          </Button>
        </div>
        <div className="secondary relative z-2 mt-auto space-y-6 px-4">
          {feature && (
            <div className="flex flex-col gap-2">
              <hr className="w-full border-neutral-300 pb-4" />
              {feature.links?.map((item) => (
                <Button
                  key={item.id}
                  variant="link"
                  icon={false}
                  asChild
                  className="ml-4 text-base! font-normal text-secondary-foreground"
                >
                  {item.link.type === 'reference' ? (
                    <CMSLink {...item.link} />
                  ) : (
                    <Link
                      href={item.link.url || '/'}
                      target={item.link.newTab ? '_blank' : '_self'}
                      rel={item.link.newTab ? 'noopener noreferrer' : undefined}
                    >
                      {item.link.label}
                      {item.link.newTab && <ExternalLinkIcon className="size-3" />}
                    </Link>
                  )}
                </Button>
              ))}
            </div>
          )}
          {secondary?.map((column) => (
            <div key={column.id} className="flex flex-col gap-2">
              <hr className="w-full border-neutral-300 pb-4" />
              {column.links?.map((item) => (
                <Button
                  key={item.id}
                  variant="link"
                  icon={false}
                  asChild
                  className="ml-4 text-base! font-normal text-secondary-foreground"
                >
                  {item.link.type === 'reference' ? (
                    <CMSLink {...item.link} />
                  ) : (
                    <Link
                      href={item.link.url || '/'}
                      target={item.link.newTab ? '_blank' : '_self'}
                      rel={item.link.newTab ? 'noopener noreferrer' : undefined}
                    >
                      {item.link.label}
                      {item.link.newTab && <ExternalLinkIcon className="size-3" />}
                    </Link>
                  )}
                </Button>
              ))}
            </div>
          ))}
        </div>

        {hasOpened && (
          <Media
            loading="eager"
            resource="/media/austin_no_blur.png"
            htmlelement={null}
            fill={true}
            pictureClassName="pointer-events-none absolute inset-0 z-0 block size-full rotate-180 overflow-hidden object-cover before:absolute before:inset-0 before:z-1 before:bg-linear-to-l before:from-background before:to-transparent"
            imgClassName="size-full object-cover object-center"
          />
        )}
      </div>
    </div>
  )
}
